import { ChevronRight, User, Bell, Globe, CalendarDays, Star, FileText, Lock, Mail, LogOut, DollarSign, Receipt } from 'lucide-react';
import { SubscriptionSettingsRow } from '@/features/subscription/components/SubscriptionSettingsRow';
import { useNavigate } from 'react-router-dom';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { LanguageSelector } from '@/components/ui/LanguageSelector';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSettings } from '@/shared/hooks/useSettings';
import { getInitials } from '@/utils/avatarUtils';

// Color constants
const COLORS = {
  bg: '#160C0C',
  text: '#FFFFFF',
  textSecondary: '#8D8D8D',
  border: '#FFFFFF0D', // white/5
  accent: '#C41E1E',
  modalBg: '#FFFFFF', // Dialog background (standardized across app)
  modalText: '#000000', // Dialog text
  modalBorder: '#E5E5E5', // Light gray border for modal
  modalTextSecondary: '#666666', // Modal secondary text (description)
  buttonCancel: '#F0F0F0', // Cancel button background
};

// -- Earnings card (instructor-only) --

function EarningsCard({
  isOnboarded,
  onClick,
}: {
  isOnboarded: boolean;
  onClick: () => void;
}) {
  return (
    <div className="mx-4 mt-4 mb-1">
      <button
        onClick={onClick}
        className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-left"
        style={{
          backgroundColor: '#281B1B',
          border: '1px solid #FFFFFF1A',
        }}
      >
        <div
          className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center"
          style={{ backgroundColor: '#C41E1E22' }}
        >
          <DollarSign className="w-5 h-5" style={{ color: '#C41E1E' }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold tracking-widest mb-0.5" style={{ color: '#C41E1E' }}>
            EARNINGS
          </p>
          <p className="text-xs leading-snug" style={{ color: '#8D8D8D' }}>
            {isOnboarded
              ? 'Your wallet is active. View your balance and transactions.'
              : 'Create your wallet and start receiving payments for roll reviews and private class through sOSSiety.'}
          </p>
        </div>
        <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#8D8D8D' }} />
      </button>
    </div>
  );
}

// -- Sub-components --

interface SettingsRowProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  rightElement?: React.ReactNode;
  onClick?: () => void;
}

const SettingsRow = ({ icon, title, subtitle, rightElement, onClick }: SettingsRowProps) => (
  <button
    className="flex items-center w-full px-5 py-4 gap-4 text-left border-b"
    style={{ borderColor: COLORS.border }}
    onClick={onClick}
  >
    <div className="flex-shrink-0 w-5 h-5 text-white">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium text-sm">{title}</p>
      {subtitle && <p style={{ color: COLORS.textSecondary }} className="text-xs mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex-shrink-0 flex items-center gap-2">
      {rightElement}
      <ChevronRight className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
    </div>
  </button>
);

interface SettingsSectionProps {
  title: string;
  children: React.ReactNode;
}

const SettingsSection = ({ title, children }: SettingsSectionProps) => (
  <div className="mt-6">
    <p
      className="px-5 text-xs font-semibold tracking-widest mb-3"
      style={{ color: COLORS.textSecondary }}
    >
      {title}
    </p>
    {children}
  </div>
);

// -- Main page --

const Settings = () => {
  const navigate = useNavigate();
  const {
    profile,
    isProfileLoading,
    userEmail,
    isInstructor,
    currentLanguage,
    isSignOutConfirmOpen,
    isPreferencesExpanded,
    handleNavigate,
    togglePreferences,
    handleSignOutRequest,
    handleSignOutConfirm,
    handleSignOutCancel,
    t,
  } = useSettings();

  // Only call hook when needed — hook is safe to call unconditionally
  const { isOnboarded } = useStripeConnect();

  const badgeLabel = isInstructor ? t('settings.badge_instructor') : t('settings.badge_practitioner');
  const initials = getInitials(profile?.full_name, '');

  return (
    <>
      <TopNavigation />

      <div className="min-h-screen w-full text-white pt-20 pb-24" style={{ backgroundColor: COLORS.bg }}>
        {/* Page title */}
        <h1 className="px-5 pt-4 pb-2 text-2xl font-bold text-white">
          {t('settings.title')}
        </h1>

        {/* Profile preview */}
        {!isProfileLoading && (
          <button
            className="flex items-center gap-4 px-5 py-4 w-full text-left"
            onClick={() => handleNavigate('/settings/edit-profile')}
          >
            <FramedAvatar
              src={profile?.profile_picture_url}
              alt={profile?.full_name}
              initials={initials}
              size={48}
              frameSize={{ w: 56, h: 62 }}
              showFrame={true}
              beltRank={profile?.belt_rank}
              isManual={true}
            />
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-base truncate">
                {profile?.full_name ?? ''}
              </p>
              <p style={{ color: COLORS.textSecondary }} className="text-sm truncate">
                {userEmail}
              </p>
              <span
                className="inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: COLORS.accent }}
              >
                {badgeLabel}
              </span>
            </div>
          </button>
        )}

        {/* EARNINGS card — instructors only */}
        {isInstructor && (
          <EarningsCard
            isOnboarded={!!isOnboarded}
            onClick={() => navigate(isOnboarded ? '/wallet' : '/settings/instructor-tools/earnings')}
          />
        )}

        {/* ACCOUNT section */}
        <SettingsSection title={t('settings.account_section')}>
          <SettingsRow
            icon={<User className="w-5 h-5" />}
            title={t('settings.profile_row')}
            subtitle={t('settings.profile_row_subtitle')}
            onClick={() => handleNavigate('/settings/edit-profile')}
          />
          <SettingsRow
            icon={<Bell className="w-5 h-5" />}
            title={t('settings.notifications_row')}
            subtitle={t('settings.notifications_row_subtitle')}
            onClick={() => handleNavigate('/settings/notifications')}
          />
          <SettingsRow
            icon={<Receipt className="w-5 h-5" />}
            title={t('settings.my_payments_row')}
            subtitle={t('settings.my_payments_row_subtitle')}
            onClick={() => handleNavigate('/my-purchases')}
          />
          <div>
            <SettingsRow
              icon={<Globe className="w-5 h-5" />}
              title={t('settings.preferences_row')}
              subtitle={t('settings.preferences_row_subtitle')}
              onClick={togglePreferences}
            />
            {isPreferencesExpanded && (
              <div
                className="px-5 py-3 border-b flex items-center justify-between"
                style={{ borderColor: COLORS.border }}
              >
                <p style={{ color: COLORS.textSecondary }} className="text-sm">
                  {t(currentLanguage === 'pt' ? 'settings.language_pt' : 'settings.language_en')}
                </p>
                <LanguageSelector variant="compact" />
              </div>
            )}
          </div>
        </SettingsSection>

        {/* INSTRUCTOR section — only for instructors */}
        {isInstructor && (
          <SettingsSection title={t('settings.instructor_section')}>
            <SettingsRow
              icon={<CalendarDays className="w-5 h-5" />}
              title={t('settings.instructor_tools_row')}
              subtitle={t('settings.instructor_tools_row_subtitle')}
              onClick={() => handleNavigate('/settings/instructor-tools')}
            />
          </SettingsSection>
        )}

        {/* PLAN section — instructor-only.
            SOS-355 §1: practitioners must not see the section header either —
            the row inside is already gated, but an empty header was leaking. */}
        {isInstructor && (
          <SettingsSection title={t('settings.plan_section')}>
            <SubscriptionSettingsRow />
          </SettingsSection>
        )}

        {/* ABOUT section */}
        <SettingsSection title={t('settings.about_section')}>
          <SettingsRow
            icon={<FileText className="w-5 h-5" />}
            title={t('settings.terms_of_use')}
            onClick={() => handleNavigate('/terms-of-use')}
          />
          <SettingsRow
            icon={<Lock className="w-5 h-5" />}
            title={t('settings.privacy_policy')}
            onClick={() => handleNavigate('/privacy-policies')}
          />
          <SettingsRow
            icon={<Mail className="w-5 h-5" />}
            title={t('settings.contact_us')}
            onClick={() => handleNavigate('/contact')}
          />
        </SettingsSection>

        {/* Sign Out */}
        <div className="px-4 mt-6">
          <button
            className="flex items-center justify-center gap-2 w-full py-4 rounded-xl font-semibold text-sm border"
            style={{
              color: COLORS.accent,
              borderColor: `${COLORS.accent}4D`, // 30% opacity
              backgroundColor: `${COLORS.accent}1A`, // 10% opacity
            }}
            onClick={handleSignOutRequest}
          >
            <LogOut className="w-4 h-4" />
            {t('settings.sign_out')}
          </button>
        </div>
      </div>

      <BottomNavigation />

      {/* Sign out confirmation */}
      <AlertDialog open={isSignOutConfirmOpen} onOpenChange={handleSignOutCancel}>
        <AlertDialogContent
          className="border w-[90vw] max-w-md p-6"
          style={{
            backgroundColor: COLORS.modalBg,
            borderColor: COLORS.modalBorder,
            borderRadius: '1rem',
          }}
        >
          <div className="space-y-5">
            {/* Icon and Header */}
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <LogOut className="w-6 h-6 text-red-600" />
                </div>
              </div>
              <div>
                <AlertDialogTitle style={{ color: COLORS.modalText }} className="text-lg mb-2">
                  {t('settings.sign_out_confirm_title')}
                </AlertDialogTitle>
                <AlertDialogDescription style={{ color: COLORS.modalTextSecondary }} className="text-sm">
                  {t('settings.sign_out_confirm_description')}
                </AlertDialogDescription>
              </div>
            </div>

            {/* Action Buttons */}
            <AlertDialogFooter className="!flex-col !justify-start !space-x-0 gap-2">
              <AlertDialogCancel
                className="mt-0 h-11 w-full rounded-full border-0 font-medium"
                style={{ backgroundColor: COLORS.buttonCancel, color: COLORS.modalText }}
                onClick={handleSignOutCancel}
              >
                {t('settings.sign_out_cancel_button')}
              </AlertDialogCancel>
              <AlertDialogAction
                className="h-11 w-full rounded-full border-0 font-medium text-white"
                style={{ backgroundColor: COLORS.accent }}
                onClick={handleSignOutConfirm}
              >
                {t('settings.sign_out_confirm_button')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Settings;
